/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */
import axios from 'axios'
import { getApiUrl } from '@utils/api-helpers'
import { logger } from '@plugins/logger'
import { isEmptyOrNullOrUndefined } from '@utils/string-helpers'
import { SPA_SOFT_ROUTE_NAMES } from '@components/07.spa-soft/spa-soft/routes/spa-soft-routes'
import { formatDateZone } from '@utils/date-helpers'

/**
 * @typedef {Object} Treatment
 * @property {string} treatmentId
 * @property {number} price
 * @property {string} uuid
 * @property {Object} selectedTimeSlot
 */

/**
 * @typedef {Object} GuestsSelectedData
 * @property {string|null} selectedCategory
 * @property {Treatment[]} selectedTreatments
 */

/**
 * @typedef {Object} State
 * @property {number} step
 * @property {boolean} ready
 * @property {string|null} nextStepLabel
 * @property {number|null} nextStepIndex
 * @property {string} nextStepClass
 * @property {string|null} hotelCode
 * @property {Object.<number, GuestData>} guestsSelectedData
 * @property {number|null} numberOfGuests
 * @property {string|null} selectedDate
 * @property {string|null} preferredStartTime
 * @property {boolean} isSpaLoading
 * @property {Array} validCategories
 * @property {Object} validTreatments
 * @property {Object|null} selectedItinerary
 * @property {Array} availableItinerary
 * @property {Array} itineraryErrors
 * @property {Array} genderOptions
 * @property {Object|null} propertyData
 * @property {boolean} hasCancellationError
 * @property {Object|null} reservationDetailsData
 */

// Constants
const SESSION_STORAGE_KEYS = {
  GUESTS_DATA: 'guestsSelectedData',
  NUMBER_OF_GUESTS: 'numberOfGuests',
  SELECTED_DATE: 'selectedDate',
  PREFERRED_START_TIME: 'preferredStartTime',
  EXPIRATION_TIME: 'spaSoftExpirationTime',
  SELECTED_ITINERARY: 'selectedItinerary',
  PROPERTY_DATA: 'propertyData',
  GENDER_OPTIONS: 'genderOptions',
  RESERVATION_DETAILS_DATA: 'reservationDetailsData',
  SPA_PURCHASE_TRACKED: 'spaPurchaseTracked'
}

const spaSoftSteps = [
  SPA_SOFT_ROUTE_NAMES.SPA_SOFT_TREATMENTS_AND_DATE,
  SPA_SOFT_ROUTE_NAMES.SPA_SOFT_ITINERARY_SELECTION,
  SPA_SOFT_ROUTE_NAMES.SPA_SOFT_CHECK_OUT
]
const spaSoftStepsLength = spaSoftSteps.length

// Session storage helper
const sessionStorageHelper = {
  get(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key))
    } catch {
      return null
    }
  },
  set(key, value) {
    sessionStorage.setItem(key, JSON.stringify(value))
  }
}

// Router variables
let router = null
let route = null

// Mutation helpers
const mutationHelpers = {
  /**
   * Updates guest data in the state.
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload object.
   * @param {number} payload.guest - The guest number.
   * @param {Object} payload.data - The data to update.
   * @param {Object} vm - The Vue instance.
   */
  updateGuestData(state, { guest, data }, vm) {
    vm.$set(state.guestsSelectedData, guest, {
      ...state.guestsSelectedData[guest],
      ...data
    })
    sessionStorageHelper.set(SESSION_STORAGE_KEYS.GUESTS_DATA, state.guestsSelectedData)
  },

  /**
    * Updates the number of guests and their data in the state.
    * @param {Object} state - The Vuex state object.
    * @param {number} numberOfGuests - The number of guests.
    * @param {Vue} vm - The Vue instance.
  */
  updateGuestsNumber(state, numberOfGuests, vm) {
    const existingKeys = Object.keys(state.guestsSelectedData).map(Number)

    Array.from({ length: numberOfGuests }, (_, i) => i).forEach((i) => {
      if (!state.guestsSelectedData[i]) {
        vm.$set(state.guestsSelectedData, i, {
          selectedCategory: null,
          selectedTreatments: []
        })
      }
    })

    existingKeys
      .filter((key) => key >= numberOfGuests)
      .forEach((key) => {
        vm.$delete(state.guestsSelectedData, key)
      })

    state.numberOfGuests = numberOfGuests
    sessionStorageHelper.set(SESSION_STORAGE_KEYS.NUMBER_OF_GUESTS, numberOfGuests)
    sessionStorageHelper.set(SESSION_STORAGE_KEYS.GUESTS_DATA, state.guestsSelectedData)
  }
}

/**
 * Maps treatment data to a more usable format
 * @param {Array} treatments - Array of treatment objects
 * @returns {Object} Mapped treatments with treatmentId as key
 */
const mapTreatments = (treatments) => treatments.reduce((map, { treatmentId, price, ...rest }) => {
  if (treatmentId && price !== null) {
    map[treatmentId] = { ...rest, price }
  }
  return map
}, {})

/**
 * Filters and returns valid treatments
 * @param {Array} treatments - Array of treatment objects
 * @returns {Array} Filtered array of valid treatments
 */
const getValidTreatments = (treatments) => treatments
  .map((treatment) => {
    const validVariants = treatment.propertyFacilitySpaTreatmentsVariantModel.filter(
      (variant) => variant?.apiTreatmentDetails?.price != null
    )

    if (validVariants.length > 0) {
      return {
        ...treatment,
        propertyFacilitySpaTreatmentsVariantModel: validVariants,
      }
    }
    return null
  })
  .filter(Boolean) // Remove null treatments

/**
 * Generates route parameters for a given step
 * @param {number} step - The step number
 * @returns {Object} Route parameters object
 */
const generateRouteParams = (step) => {
  if (step >= 0 && step < spaSoftSteps.length) {
    return { name: spaSoftSteps[step], query: { ...route.query } }
  }
  console.error('Invalid step value. Step should be between 0 and', spaSoftSteps.length - 1)
  return { name: null }
}

/**
 * Helper function to handle router navigation
 * @param {Object} params - Route parameters
 * @param {string} method - Router method ('push' or 'replace')
 */
const routerPush = (params, method = 'push') => {
  if (router && route) {
    router[method](params)
  }
}

/**
 * Finds the preferred gender for a treatment
 * @param {Object} treatment - The treatment object
 * @param {Object} guestData - The guest data object
 * @returns {string|null} The preferred gender or null if not found
 */
const findPreferredGender = (treatment, guestData) => {
  const selectedTreatment = guestData?.selectedTreatments?.find(
    (st) => st.selectedTimeSlot?.spaSoftId === treatment.treatmentId
  )
  return selectedTreatment?.selectedTimeSlot?.preferredGender || ''
}

/**
 * Adds gender preferences to the itinerary
 * @param {Array} itinerary - The itinerary array
 * @param {Object} guestData - The guest data object
 * @returns {Array} Enriched itinerary array
 */
const addGenderPreferencesToItinerary = (guestData, itinerary = []) => itinerary.map((treatment) => ({
  ...treatment,
  preferredGender: findPreferredGender(treatment, guestData)
}))

/**
 * Adds gender preferences to the timeslot
 * @param {Object} timeslot - The timeslot object
 * @param {Object} guestsSelectedData - The guests selected data object
 * @returns {Object} Enriched timeslot object
 */
const addGenderPreferencesToTimeslot = (timeslot, index, guestsSelectedData) => ({
  ...timeslot,
  index,
  guestAvailabilities: timeslot.guestAvailabilities?.map((guestAvail, guestIndex) => ({
    ...guestAvail,
    itinerary: addGenderPreferencesToItinerary(
      guestsSelectedData[guestIndex],
      guestAvail.itinerary,
    )
  }))
})

export default {
  namespaced: true,
  state: {
    step: 0,
    ready: false,
    nextStepLabel: null,
    nextStepIndex: null,
    nextStepClass: '',
    hotelCode: null,
    guestsSelectedData: sessionStorageHelper.get(SESSION_STORAGE_KEYS.GUESTS_DATA) || {
      0: {
        selectedCategory: null,
        selectedTreatments: [],
      },
    },
    numberOfGuests: sessionStorageHelper.get(SESSION_STORAGE_KEYS.NUMBER_OF_GUESTS) || null,
    selectedDate: sessionStorageHelper.get(SESSION_STORAGE_KEYS.SELECTED_DATE) || null,
    preferredStartTime: sessionStorageHelper.get(SESSION_STORAGE_KEYS.PREFERRED_START_TIME) || null,
    propertyData: sessionStorageHelper.get(SESSION_STORAGE_KEYS.PROPERTY_DATA) || {},
    selectedItinerary: sessionStorageHelper.get(SESSION_STORAGE_KEYS.SELECTED_ITINERARY) || null,
    genderOptions: sessionStorageHelper.get(SESSION_STORAGE_KEYS.GENDER_OPTIONS) || [],
    reservationDetailsData: sessionStorageHelper.get(SESSION_STORAGE_KEYS.RESERVATION_DETAILS_DATA) || null,
    spaSoftLoading: false,
    validCategories: [],
    validTreatments: {},
    availableItinerary: [],
    itineraryErrors: null,
    isSpaLoading: false,
    hasCancellationError: false,
    cancellationMessages: [],
    selectedItineraryTrackingData: null,
    confirmationCode: null,
  },
  mutations: {
    REGISTER_ROUTER(state, { vueRouter, vueRoute }) {
      router = vueRouter
      route = vueRoute
    },

    DATA_READY(state) {
      state.ready = true
    },

    UPDATE_SPA_LOADING(state, pending = false) {
      state.isSpaLoading = pending
    },

    UPDATE_STEP(state, step) {
      state.step = step
    },

    UPDATE_STEP_BUTTON(state, { label, step, customClass = '' }) {
      state.nextStepLabel = label
      state.nextStepIndex = step
      state.nextStepClass = customClass
    },

    UPDATE_VALID_CATEGORIES(state, { validCategories }) {
      state.validCategories = validCategories
    },

    RESET_VALID_TREATMENTS(state) {
      state.validTreatments = {}
    },

    UPDATE_VALID_TREATMENTS_BY_CATEGORY(state, { validTreatments, categoryId }) {
      if (!state.validTreatments[categoryId]) {
        this._vm.$set(state.validTreatments, categoryId, [])
      }
      this._vm.$set(state.validTreatments, categoryId, [...validTreatments])
    },

    REGISTER_HOTEL_CODE(state, hotelCode) {
      state.hotelCode = hotelCode
    },

    REGISTER_PROPERTY_DATA(state, propertyData) {
      state.propertyData = propertyData
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.PROPERTY_DATA, propertyData)
    },

    SET_NUMBER_OF_GUESTS(state, numberOfGuests) {
      mutationHelpers.updateGuestsNumber(state, numberOfGuests, this._vm)
    },

    ADD_TREATMENT_TO_GUEST(state, { guest, treatment }) {
      if (treatment) {
        state.guestsSelectedData[guest].selectedTreatments.push(treatment)
        sessionStorageHelper.set(SESSION_STORAGE_KEYS.GUESTS_DATA, state.guestsSelectedData)
      }
    },

    REMOVE_TREATMENT_FROM_GUEST(state, { guest, treatmentId }) {
      if (state.guestsSelectedData[guest]?.selectedTreatments) {
        state.guestsSelectedData[guest].selectedTreatments = state.guestsSelectedData[guest].selectedTreatments.filter(
          (treatment) => treatment.treatmentId !== treatmentId
        )
        sessionStorageHelper.set(SESSION_STORAGE_KEYS.GUESTS_DATA, state.guestsSelectedData)
      }
    },

    SET_SELECTED_TREATMENTS(state, { guest, selectedTreatments }) {
      if (state.guestsSelectedData[guest]) {
        state.guestsSelectedData[guest].selectedTreatments = selectedTreatments
        sessionStorageHelper.set(SESSION_STORAGE_KEYS.GUESTS_DATA, state.guestsSelectedData)
      }
    },

    RESET_GUEST_SELECTED_TREATMENT(state, { guest }) {
      mutationHelpers.updateGuestData(
        state,
        {
          guest,
          data: { selectedCategory: null, selectedTreatments: [] }
        },
        this._vm
      )
    },

    UPDATE_GUEST_SELECTED_CATEGORY(state, { guest, selectedCategory }) {
      mutationHelpers.updateGuestData(
        state,
        {
          guest,
          data: { selectedCategory }
        },
        this._vm
      )
    },

    UPDATE_GUEST_DATA(state, { index, field, value }) {
      // Ensure we have access to the existing guest object
      const guest = state.selectedItinerary?.guestAvailabilities?.[index]?.guest
      if (!guest) return

      // Update field directly on guest object
      guest[field] = value

      sessionStorageHelper.set(SESSION_STORAGE_KEYS.SELECTED_ITINERARY, state.selectedItinerary)
    },

    UPDATE_GUEST_SELECTED_TREATMENTS(state, { guest, selectedTreatments }) {
      mutationHelpers.updateGuestData(
        state,
        {
          guest,
          data: { selectedTreatments }
        },
        this._vm
      )
    },

    SET_SELECTED_DATE(state, selectedDate) {
      state.selectedDate = selectedDate
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.SELECTED_DATE, selectedDate)
    },

    SET_PREFERRED_START_TIME(state, preferredStartTime) {
      state.preferredStartTime = preferredStartTime
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.PREFERRED_START_TIME, preferredStartTime)
    },

    UPDATE_ITINERARY_AVAILABILITY(state, availableItinerary) {
      state.availableItinerary = availableItinerary
    },

    UPDATE_ITINERARY_ERRORS(state, errors) {
      state.itineraryErrors = errors
    },

    UPDATE_SELECTED_ITINERARY(state, selectedItinerary) {
      state.selectedItinerary = selectedItinerary
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.SELECTED_ITINERARY, selectedItinerary)
    },

    UPDATE_CONFIRMATION_CODE(state, confirmationCode) {
      state.confirmationCode = confirmationCode
    },

    REGISTER_GENDER_OPTIONS(state, options) {
      state.genderOptions = options
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.GENDER_OPTIONS, options)
    },

    RESET_ITINERARY_ERRORS(state) {
      state.itineraryErrors = null
    },

    RESET_SPA_BOOKING_DATA(state) {
      state.guestsSelectedData = {
        0: {
          selectedCategory: null,
          selectedTreatments: [],
        },
      }
      state.selectedItinerary = null
      state.selectedDate = null
      state.preferredStartTime = null
      state.numberOfGuests = 1

      sessionStorageHelper.set(SESSION_STORAGE_KEYS.GUESTS_DATA, state.guestsSelectedData)
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.SELECTED_ITINERARY, state.selectedItinerary)
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.SELECTED_DATE, state.selectedDate)
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.PREFERRED_START_TIME, state.preferredStartTime)
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.NUMBER_OF_GUESTS, state.numberOfGuests)
    },

    UPDATE_CANCELLATION_SUCCESSFUL(state, cancellationMessages = []) {
      state.cancellationMessages = cancellationMessages
    },

    UPDATE_CANCELLATION_ERROR(state, status) {
      state.hasCancellationError = status
    },

    UPDATE_RESERVATION_DETAILS_DATA(state, reservationDetailsData) {
      state.reservationDetailsData = reservationDetailsData
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.RESERVATION_DETAILS_DATA, reservationDetailsData)
    },

    UPDATE_SELECTED_ITINERARY_TRACKING_DATA(state, trackingData) {
      state.selectedItineraryTrackingData = trackingData
    },
  },

  getters: {
    getStepIndexByValue: () => (value) => Object.values(spaSoftSteps).indexOf(value),
    getStep: (state) => state.step,
    selectedTreatmentsByGuest: (state) => (guest) => state.guestsSelectedData[guest]?.selectedTreatments || [],
    hasSomeCategorySelected: (state) => Object.values(state.guestsSelectedData).some((guest) => guest?.selectedCategory),
    hasSomeTreatmentSelected: (state) => Object.values(state.guestsSelectedData).some((guest) => guest?.selectedTreatments?.length > 0),
    shouldShowMastheadAndTitle: (state, getters) => {
      if (state.step !== 0) return false
      return !(getters.hasSomeCategorySelected || getters.hasSomeTreatmentSelected)
    },
    numberOfGuests: (state) => state.numberOfGuests,
    selectedDate: (state) => state.selectedDate,
    preferredStartTime: (state) => state.preferredStartTime,
    guestsSelectedData: (state) => state.guestsSelectedData,
    propertyCurrency: (state) => (options = { isReservation: false }) => {
      const { isReservation = false } = options
      return isReservation ? state.reservationDetailsData.propertyData?.currency?.isoCode : state.propertyData?.currency?.isoCode
    },
    hasGuestsWithNoSelectedTreatments: (state) => {
      const guests = Object.values(state.guestsSelectedData)
      return guests.length === 0
        || guests.some((guest) => !guest?.selectedTreatments?.length)
    },
    hasItinerarySelected: (state) => state.selectedItinerary !== null,
    getMatchingYieldPrice: (state) => (apiTreatmentDetails, isReservation) => {
      if (apiTreatmentDetails?.yieldPrices.length === 0) {
        return null
      }

      const date = isReservation ? state?.reservationDetailsData?.date : state?.selectedDate

      const selectedDate = new Date(date.replace(/-/g, '/'))
      const yieldPrices = apiTreatmentDetails?.yieldPrices || []

      return yieldPrices.find((yieldPrice) => {
        const activeFrom = new Date(yieldPrice.activeFrom)
        const activeUntil = new Date(yieldPrice.activeUntil)
        const isWithinDateRange = selectedDate >= activeFrom && selectedDate <= activeUntil

        if (yieldPrice.scheduledDays) {
          const selectedDay = selectedDate.toLocaleDateString('en-US', { weekday: 'long' })
          const scheduledDays = yieldPrice.scheduledDays.split(', ').map((day) => day.trim())
          return isWithinDateRange && scheduledDays.includes(selectedDay)
        }

        return isWithinDateRange
      })
    },

    getTreatmentPrice: (state, getters, rootState) => ({ treatment, checkTaxes = false, isReservation = false }) => {
      if (!treatment?.apiTreatmentDetails?.price) return 0

      const matchingYieldPrice = getters.getMatchingYieldPrice(treatment?.apiTreatmentDetails, isReservation)

      let price = matchingYieldPrice
        ? matchingYieldPrice.price
        : treatment.apiTreatmentDetails.price.totalCalculated || 0

      const inclusiveRate = isReservation
        ? state.reservationDetailsData.propertyData?.inclusiveRate
        : state.propertyData?.inclusiveRate

      if (checkTaxes && rootState?.global?.isCalifornia && !inclusiveRate) {
        if (matchingYieldPrice) {
          const taxTotal = matchingYieldPrice.tax?.reduce((sum, tax) => sum + (tax.total || 0), 0) || 0
          price += taxTotal
        } else {
          price += treatment?.apiTreatmentDetails?.price?.taxesValue || 0
        }
      }

      return price
    },
    getTreatmentPriceFormatted:
      (state, getters, rootState, rootGetters) => ({ treatment, checkTaxes = false, isReservation = false }) => rootGetters['currency/getDisplayPrice']({
        price: getters.getTreatmentPrice({ treatment, checkTaxes, isReservation }),
        withIsoCode: true,
        fromCurrency: getters.propertyCurrency({ isReservation }),
        toCurrency: rootGetters['currency/orSelected'](getters.propertyCurrency({ isReservation }))
      }),
    calculateTotal: (state, getters, rootState) => (options = { type: 'totalCalculated', isReservation: false }) => {
      const { type, isReservation = false } = options

      return Object.values(isReservation ? state.reservationDetailsData.guestAvailabilities : state.guestsSelectedData).reduce((total, guestData) => {
        const guestItinerary = isReservation ? guestData.itinerary : guestData.selectedTreatments

        const guestValue = guestItinerary.reduce((treatmentValue, treatment) => {
          const matchingYieldPrice = getters.getMatchingYieldPrice(treatment?.selectedTimeSlot?.apiTreatmentDetails, isReservation)

          const inclusiveRate = isReservation
            ? state.reservationDetailsData.propertyData?.inclusiveRate
            : state.propertyData?.inclusiveRate

          if (matchingYieldPrice) {
            if (type === 'taxesValue') {
              return treatmentValue + (matchingYieldPrice.tax?.reduce((sum, tax) => sum + (tax.total || 0), 0) || 0)
            }

            if (!inclusiveRate) {
              const taxTotal = matchingYieldPrice.tax?.reduce((sum, tax) => sum + (tax.total || 0), 0) || 0
              return treatmentValue + matchingYieldPrice.price + taxTotal
            }
            return treatmentValue + matchingYieldPrice.price
          }

          return treatmentValue + (treatment?.selectedTimeSlot?.apiTreatmentDetails?.price?.[type] || 0)
        }, 0)

        return total + guestValue
      }, 0)
    },
    getTimeslotTimeRange: (state) => (guestAvailabilities) => {
      if (!guestAvailabilities || guestAvailabilities.length === 0) return 'No times available'

      const allItineraries = guestAvailabilities.flatMap((guest) => guest.itinerary)

      if (allItineraries.length === 0) return 'No itineraries available'

      const startTimes = allItineraries.map((itinerary) => new Date(itinerary.start))
      const endTimes = allItineraries.map((itinerary) => new Date(itinerary.end))

      const earliestStart = new Date(Math.min(...startTimes))
      const latestEnd = new Date(Math.max(...endTimes))

      return `${formatDateZone(earliestStart.toISOString(), null, 'h:mm a').replace(':00', '')} - ${formatDateZone(latestEnd.toISOString(), null, 'h:mm a').replace(':00', '')}`
    },

    getTaxesList: (state, getters) => (options = { isReservation: false }) => {
      const { isReservation = false } = options

      return Object.values(isReservation ? state.reservationDetailsData.guestAvailabilities : state.guestsSelectedData).reduce((acc, guest) => {
        const guestItinerary = isReservation ? guest.itinerary : guest.selectedTreatments

        guestItinerary.forEach((treatment) => {
          const matchingYieldPrice = getters.getMatchingYieldPrice(treatment?.selectedTimeSlot?.apiTreatmentDetails, isReservation)
          if (matchingYieldPrice?.tax?.length > 0) {
            acc.push({
              treatmentName: treatment?.sitecoreTreatmentDetails?.title,
              taxes: matchingYieldPrice.tax
            })
          } else if (treatment?.selectedTimeSlot?.apiTreatmentDetails?.price?.taxes?.length > 0) {
            acc.push({
              treatmentName: treatment?.sitecoreTreatmentDetails?.title,
              taxes: treatment?.selectedTimeSlot?.apiTreatmentDetails?.price?.taxes
            })
          }
        })
        return acc
      }, [])
    },
    getTotalTaxes: (state, getters, rootState, rootGetters) => (options = {
      isReservation: false, formatted: false, noSymbol: false, withIsoCode: true
    }) => {
      const {
        isReservation = false, formatted = false, noSymbol = false, withIsoCode = true
      } = options
      if (formatted) {
        return rootGetters['currency/getDisplayPrice']({
          price: getters.calculateTotal({ type: 'taxesValue', isReservation }),
          withIsoCode,
          fromCurrency: getters.propertyCurrency({ isReservation }),
          toCurrency: rootGetters['currency/orSelected'](getters.propertyCurrency({ isReservation })),
          noSymbol
        })
      }
      return getters.calculateTotal({ type: 'taxesValue', isReservation })
    },
    getTotalCost: (state, getters, rootState, rootGetters) => (options = {
      isReservation: false, formatted: false, noSymbol: false, withIsoCode: true
    }) => {
      const {
        isReservation = false, formatted = false, noSymbol = false, withIsoCode = true
      } = options
      if (formatted) {
        return rootGetters['currency/getDisplayPrice']({
          price: getters.calculateTotal({ type: 'totalCalculated', isReservation }),
          withIsoCode,
          fromCurrency: getters.propertyCurrency({ isReservation }),
          toCurrency: rootGetters['currency/orSelected'](getters.propertyCurrency({ isReservation })),
          noSymbol
        })
      }
      return getters.calculateTotal({ type: 'totalCalculated', isReservation })
    },

    getStartTime: (state) => (treatment, guestIndex, isReservation = false) => {
      const startDate = isReservation
        ? state.reservationDetailsData?.guestAvailabilities[guestIndex]?.itinerary?.find(
          (t) => t.treatmentId === treatment.selectedTimeSlot.spaSoftId
        )?.start
        : state.selectedItinerary?.guestAvailabilities[guestIndex]?.itinerary?.find(
          (t) => t.treatmentId === treatment.selectedTimeSlot.spaSoftId
        )?.start
      const formattedStartDate = startDate ? formatDateZone(startDate, null, 'h:mm a') : ''
      return formattedStartDate.replace(':00', '')
    },
    getEndTime: (state) => (treatment, guestIndex) => {
      const endDate = state.selectedItinerary?.guestAvailabilities[guestIndex]?.itinerary?.find(
        (t) => t.treatmentId === treatment.selectedTimeSlot.spaSoftId
      )?.end
      const formattedEndDate = endDate ? formatDateZone(endDate, null, 'h:mm a') : ''
      return formattedEndDate.replace(':00', '')
    },
    getGenderPreference: (state) => (treatment) => state.genderOptions.find((option) => option.value === treatment.selectedTimeSlot.preferredGender)?.label || '',

    isReservationCancelled: (state) => Object.values(state.reservationDetailsData?.guestAvailabilities || {}).every((guest) => guest.itinerary.every((treatment) => !treatment.isActive)),
    hasCancellationError: (state) => state.hasCancellationError,
    getBookingTrackingSpaSoft: (state, getters, rootState, rootGetters) => (eventData) => {
      const userProfile = rootState.auth.user
      let userParams = {}
      if (userProfile) {
        userParams = {
          user: {
            accountType: rootGetters['auth/fanLevel'],
            accountNumber: rootGetters['auth/userId'] ? rootGetters['auth/userId'].toLowerCase() : '',
            loginState: 'logged in',
            ireckonuID: userProfile?.personId?.toString() || null,
            email: userProfile?.email || null,
          }
        }
      }
      return {
        ...eventData,
        ...userParams
      }
    }
  },

  actions: {
    async handleApiError({ dispatch }, { error, actionName = 'unknown' }) {
      await dispatch('overlay/deactivateOverlay', 'spa-soft', { root: true })
      logger.error(`API Error in ${actionName}`)
      logger.error(error)
    },

    registerRouter({ commit }, { vueRouter, vueRoute }) {
      commit('REGISTER_ROUTER', { vueRouter, vueRoute })
    },

    registerHotelCode({ commit }, hotelCode) {
      commit('REGISTER_HOTEL_CODE', hotelCode)
    },

    registerPropertyData({ commit }, propertyData) {
      commit('REGISTER_PROPERTY_DATA', propertyData)
    },

    updateRoute(_, vueRoute) {
      route = vueRoute
    },

    updateNextStepButton({ commit }, { label, step, customClass }) {
      commit('UPDATE_STEP_BUTTON', { label, step, customClass })
    },

    nextStep({ state, commit }, method = 'push') {
      if (state.step >= spaSoftStepsLength - 1) return

      commit('UPDATE_STEP', state.step + 1)
      routerPush(generateRouteParams(state.step), method)
    },

    prevStep({ state, commit }, method = 'push') {
      if (state.step <= 0) return

      commit('UPDATE_STEP', state.step - 1)
      routerPush(generateRouteParams(state.step), method)
    },

    goToStep({ commit, state, getters }, step) {
      const targetStep = typeof step === 'string'
        ? getters.getStepIndexByValue(step)
        : step

      if (targetStep < 0 || targetStep > spaSoftStepsLength - 1) return

      commit('UPDATE_STEP', targetStep)
      routerPush(generateRouteParams(state.step))
    },

    updateStep({ commit, getters }, step) {
      const targetStep = typeof step === 'string'
        ? getters.getStepIndexByValue(step)
        : step

      if (targetStep >= 0 && targetStep <= spaSoftStepsLength - 1) {
        commit('UPDATE_STEP', targetStep)
      }
    },

    async validateSpaSoftStep({ getters, commit }) {
      const stepIndex = getters.getStepIndexByValue(route.name)
      if (stepIndex > -1) {
        commit('UPDATE_STEP', stepIndex)
      }
    },

    async validateSpaSoftRoute({
      state, commit, getters, dispatch
    }) {
      const isFirstLoad = !state.ready
      if (isFirstLoad) {
        commit('DATA_READY')
      }

      if (getters.hasGuestsWithNoSelectedTreatments && state.step !== 0) {
        routerPush(generateRouteParams(0))
        return
      }

      if (!isFirstLoad && state.step === 0 && !getters.hasGuestsWithNoSelectedTreatments) {
        const availableItineraries = await dispatch('checkItineraryAvailability')
        if (availableItineraries.length > 0) {
          routerPush(generateRouteParams(1))
        }
      }
    },

    resetSpaBookingData({ commit }) {
      commit('RESET_SPA_BOOKING_DATA')
    },

    async getSpaSoftData({
      commit, state, dispatch, rootGetters
    }) {
      if (!state.hotelCode) {
        throw new Error('No hotel selected')
      }

      try {
        commit('UPDATE_SPA_LOADING', true)
        await dispatch('overlay/activateOverlay', 'spa-soft', { root: true })
        dispatch('registerPropertyData', {})

        const { data } = await axios.get(
          getApiUrl(`/api/v1/spasoft/${state.hotelCode}`)
        )
        dispatch('registerPropertyData', data)

        await dispatch('getSpaAvailableCategories', { skipLoading: true })
      } catch (error) {
        await dispatch('handleApiError', { error, actionName: 'getSpaSoftData' })
      } finally {
        commit('UPDATE_SPA_LOADING', false)
        await dispatch('overlay/deactivateOverlay', 'spa-soft', { root: true })
      }
    },

    async getSpaAvailableCategories({ state, commit, dispatch }, { skipLoading = false } = {}) {
      try {
        if (!skipLoading) {
          commit('UPDATE_SPA_LOADING', true)
          await dispatch('overlay/activateOverlay', 'spa-soft', { root: true })
        }

        commit('RESET_VALID_TREATMENTS')

        const { data } = await axios.get(
          getApiUrl(`/api/v1/spasoft/${state.hotelCode}/categories`)
        )

        // Categories from Sitecore
        const categoriesMap = new Map(
          state.propertyData?.propertyFacilitySpaCategoriesModel?.map((item) => {
            if (item?.spaSoftId) {
              return [item.spaSoftId, item]
            }
            return null
          }).filter(Boolean)
        )

        const validCategories = data.categories
          .filter((category) => {
            const sitecoreCategory = categoriesMap.get(category.categoryId)

            // Check if category has at least one treatment with a valid spaSoftId
            return sitecoreCategory?.propertyFacilitySpaTreatments?.some((treatment) => treatment.propertyFacilitySpaTreatmentsVariantModel?.some((variant) => variant.spaSoftId && variant.spaSoftId !== ''))
          })
          .map((category) => {
            const sitecoreCategory = categoriesMap.get(category.categoryId)
            return {
              ...sitecoreCategory,
              title: !isEmptyOrNullOrUndefined(sitecoreCategory.title)
                ? sitecoreCategory.title
                : category.name
            }
          })

        commit('UPDATE_VALID_CATEGORIES', { validCategories })
      } catch (error) {
        await dispatch('handleApiError', { error, actionName: 'getSpaAvailableCategories' })
      } finally {
        if (!skipLoading) {
          commit('UPDATE_SPA_LOADING', false)
          await dispatch('overlay/deactivateOverlay', 'spa-soft', { root: true })
        }
      }
    },

    async getAvailableTreatmentsByCategoryId({ commit, state, dispatch }, { categoryId }) {
      if (!state.hotelCode || !categoryId) {
        throw new Error(
          !state.hotelCode ? 'No hotel selected' : 'No category selected'
        )
      }

      const categorySelected = state.propertyData?.propertyFacilitySpaCategoriesModel?.find(
        (category) => category.spaSoftId === categoryId
      )

      if (!categorySelected) {
        throw new Error('Category not found in Categories from Sitecore')
      }

      try {
        commit('UPDATE_SPA_LOADING', true)
        await dispatch('overlay/activateOverlay', 'spa-soft', { root: true })

        const { data } = await axios.post(
          getApiUrl(`/api/v1/spasoft/${state.hotelCode}/treatments`),
          {
            categoryId,
            inclusiveRate: state.propertyData?.inclusiveRate
          }
        )

        const spaSoftTreatmentsMap = mapTreatments(data.treatments)

        categorySelected.propertyFacilitySpaTreatments.forEach((treatment) => {
          treatment.propertyFacilitySpaTreatmentsVariantModel.forEach((variant) => {
            if (variant.spaSoftId in spaSoftTreatmentsMap) {
              variant.apiTreatmentDetails = spaSoftTreatmentsMap[variant.spaSoftId]
            }
          })
        })

        const validTreatments = getValidTreatments(
          categorySelected.propertyFacilitySpaTreatments
        )

        commit('UPDATE_VALID_TREATMENTS_BY_CATEGORY', {
          validTreatments,
          categoryId: categorySelected.spaSoftId,
        })
      } catch (error) {
        await dispatch('handleApiError', { error, actionName: 'getAvailableTreatmentsByCategoryId' })
      } finally {
        commit('UPDATE_SPA_LOADING', false)
        await dispatch('overlay/deactivateOverlay', 'spa-soft', { root: true })
      }
    },

    // Session management actions
    checkAndClearSession() {
      const expirationTime = sessionStorageHelper.get(SESSION_STORAGE_KEYS.EXPIRATION_TIME)
      if (expirationTime && Date.now() > Number(expirationTime)) {
        sessionStorage.clear()
        window.location.reload()
      }
    },

    handleSessionExpiration({ dispatch }) {
      dispatch('checkAndClearSession')
      const newExpirationTime = Date.now() + 60 * 60 * 1000 // 60 minutes
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.EXPIRATION_TIME, newExpirationTime)

      if (!this.expirationInterval) {
        this.expirationInterval = setInterval(() => {
          dispatch('checkAndClearSession')
        }, 60 * 1000) // Check every minute
      }
    },

    // Guest management actions
    updateNumberOfGuests({ commit }, numberOfGuests) {
      commit('SET_NUMBER_OF_GUESTS', numberOfGuests)
    },

    updateGuestSelectedCategory({ commit }, { guest, selectedCategory }) {
      commit('UPDATE_GUEST_SELECTED_CATEGORY', { guest, selectedCategory })
    },

    updateGuestSelectedTreatments({ commit }, { guest, selectedTreatments }) {
      commit('UPDATE_GUEST_SELECTED_TREATMENTS', { guest, selectedTreatments })
    },

    updateSelectedDate({ commit }, selectedDate) {
      commit('SET_SELECTED_DATE', selectedDate)
    },

    updatePreferredStartTime({ commit }, preferredStartTime) {
      commit('SET_PREFERRED_START_TIME', preferredStartTime)
    },

    addTreatment({ commit }, { guest, treatment }) {
      if (!treatment) return
      commit('ADD_TREATMENT_TO_GUEST', { guest, treatment })
    },

    resetSelectedTreatment({ commit }, { guest }) {
      commit('RESET_GUEST_SELECTED_TREATMENT', { guest })
    },

    removeTreatment({ commit, state }, { guest, uuid }) {
      if (!uuid || !state.guestsSelectedData[guest]) return

      const treatments = [...state.guestsSelectedData[guest].selectedTreatments]
      const index = treatments.findIndex((treatment) => treatment.uuid === uuid)

      if (index === -1) return

      treatments.splice(index, 1)
      const reorderedTreatments = treatments.map((treatment, idx) => ({
        ...treatment,
        order: idx + 1
      }))

      commit('UPDATE_GUEST_SELECTED_TREATMENTS', {
        guest,
        selectedTreatments: reorderedTreatments
      })
    },

    moveTreatment({ commit, state }, { guest, uuid, direction }) {
      if (!state.guestsSelectedData[guest]) return

      const treatments = [...state.guestsSelectedData[guest].selectedTreatments]
      const index = treatments.findIndex((treatment) => treatment.uuid === uuid)

      if (index === -1) return

      const newIndex = direction === 'up' ? index - 1 : index + 1
      if (newIndex < 0 || newIndex >= treatments.length) return

      const updatedTreatments = [...treatments]
      const [movedTreatment] = updatedTreatments.splice(index, 1)
      updatedTreatments.splice(newIndex, 0, movedTreatment)

      const reorderedTreatments = updatedTreatments.map((treatment, idx) => ({
        ...treatment,
        order: idx + 1
      }))

      commit('UPDATE_GUEST_SELECTED_TREATMENTS', {
        guest,
        selectedTreatments: reorderedTreatments
      })
    },

    updatePreferredGender({ state, commit }, { guest, uuid, gender }) {
      if (!state.guestsSelectedData[guest]) return

      const selectedTreatments = state.guestsSelectedData[guest].selectedTreatments.map(
        (treatment) => (treatment.uuid === uuid
          ? {
            ...treatment,
            selectedTimeSlot: {
              ...treatment.selectedTimeSlot,
              preferredGender: gender
            }
          }
          : treatment)
      )

      commit('SET_SELECTED_TREATMENTS', { guest, selectedTreatments })
    },

    checkIfTreatmentsAreMissing({ state }, { requestPayload, response }) {
      const { spaItineraries } = requestPayload

      if (!response?.timetableOptions) {
        throw new Error('Invalid response structure')
      }

      const { timetableOptions } = response
      const differentData = {}

      if (timetableOptions.length === 0) {
        spaItineraries.forEach((guestAvailability, guestIndex) => {
          differentData[guestIndex] = guestAvailability.treatments.map((t) => t.treatmentId)
        })
      } else {
        timetableOptions.forEach((option) => {
          option.guestAvailabilities.forEach((guestAvailability, guestIndex) => {
            const sentTreatments = spaItineraries[guestIndex].treatments.map((t) => t.treatmentId)
            const receivedTreatments = guestAvailability.itinerary.map((t) => t.treatmentId)

            const missingTreatments = sentTreatments.filter((treatmentId) => !receivedTreatments.includes(treatmentId))

            if (!differentData[guestIndex]) {
              differentData[guestIndex] = []
            }
            if (missingTreatments.length > 0) {
              differentData[guestIndex] = missingTreatments
            }
          })
        })
      }

      return differentData
    },

    async checkItineraryAvailability({
      commit, state, dispatch, rootState, rootGetters
    }) {
      commit('UPDATE_SELECTED_ITINERARY', null)
      commit('UPDATE_SPA_LOADING', true)

      const mapGuestData = (guestIndex, guest) => {
        const isFirstGuest = Number(guestIndex) === 0
        const shouldIncludeUserData = isFirstGuest && rootGetters['auth/isAuthenticated'] && rootGetters['auth/haveProfileData']
        const user = rootState?.auth?.user

        return {
          iruProfileId: shouldIncludeUserData && user?.personId ? user?.personId : '',
          firstName: shouldIncludeUserData && user?.firstName ? user?.firstName : '',
          lastName: shouldIncludeUserData && user?.lastName ? user?.lastName : '',
          email: shouldIncludeUserData && user?.email ? user?.email : '',
          spaSoftProfileId: '',
          treatments: guest.selectedTreatments
            .filter((treatment) => treatment.selectedTimeSlot)
            .map((treatment) => ({
              treatmentId: treatment.selectedTimeSlot.spaSoftId,
              preferredGender: treatment.selectedTimeSlot.preferredGender,
              addOns: []
            }))
        }
      }

      const availabilityBody = {
        date: state.selectedDate,
        preferredTime: state.preferredStartTime,
        spaItineraries: Object.entries(state.guestsSelectedData)
          .map(([guestIndex, guest]) => mapGuestData(guestIndex, guest)),
        timeSlotsSuggestions: state.propertyData?.timeSlotsSuggestions,
        minutesBetweenTimeSlots: state.propertyData?.minutesBetweenTimeSlots
      }

      try {
        await dispatch('overlay/activateOverlay', 'spa-soft', { root: true })

        const { data } = await axios.post(
          getApiUrl(`/api/v1/spasoft/${state.hotelCode}/availability`),
          availabilityBody,
        )

        const missingTreatments = await dispatch('checkIfTreatmentsAreMissing', { requestPayload: availabilityBody, response: data })
        const hasMissingTreatments = Object.keys(missingTreatments).some((key) => missingTreatments[key].length > 0)

        if (hasMissingTreatments) {
          commit('UPDATE_ITINERARY_AVAILABILITY', [])
          commit('UPDATE_ITINERARY_ERRORS', { type: 'missingTreatments', data: missingTreatments })
          return []
        }

        // We need to enrich the API response with preferredGender from the originally selected treatments
        // This data is required during checkout but not returned by the availability endpoint
        // The preferredGender is matched based on the treatmentId and carried forward to maintain the guest's preferences
        const availableTimeslots = data.timetableOptions.map((timeslot, index) => addGenderPreferencesToTimeslot(timeslot, index, state.guestsSelectedData))
        commit('UPDATE_ITINERARY_AVAILABILITY', availableTimeslots)
        commit('UPDATE_ITINERARY_ERRORS', null)
        return data.timetableOptions
      } catch (error) {
        if (error?.response?.data?.errors?.NoAvailability) {
          const missingTreatments = await dispatch('checkIfTreatmentsAreMissing', { requestPayload: availabilityBody, response: { timetableOptions: [] } })
          commit('UPDATE_ITINERARY_ERRORS', { type: 'missingTreatments', data: missingTreatments })
        } else if (error?.response?.data?.errors?.Woops) {
          commit('UPDATE_ITINERARY_ERRORS', { type: 'woops', data: { message: error.response.data.errors.Woops } })
        } else {
          commit('UPDATE_ITINERARY_ERRORS', { type: 'unknown', data: { message: 'Internal Error' } })
        }
        commit('UPDATE_ITINERARY_AVAILABILITY', [])
        await dispatch('handleApiError', { error, actionName: 'checkItineraryAvailability' })
        return []
      } finally {
        commit('UPDATE_SPA_LOADING', false)
        await dispatch('overlay/deactivateOverlay', 'spa-soft', { root: true })
      }
    },

    registerGenderOptions({ commit }, options) {
      commit('REGISTER_GENDER_OPTIONS', options)
    },

    selectItinerary({ commit }, itinerary) {
      commit('UPDATE_SELECTED_ITINERARY', itinerary)
    },

    resetItineraryErrors({ commit }) {
      commit('RESET_ITINERARY_ERRORS')
    },

    generateReservationDetailsData({ state, commit, getters }) {
      const guestAvailabilities = state.selectedItinerary.guestAvailabilities
        .map((guestAvailability) => ({
          guest: guestAvailability.guest,
          itinerary: guestAvailability.itinerary.map((treatment) => {
            // Find the corresponding treatment from guestsSelectedData
            const guestIndex = Object.keys(state.guestsSelectedData).find(
              (index) => state.guestsSelectedData[index].selectedTreatments.some(
                (t) => t.selectedTimeSlot
                  && t.selectedTimeSlot.spaSoftId === treatment.treatmentId
              )
            )

            const guestData = state.guestsSelectedData[guestIndex]
            const selectedTreatment = guestData?.selectedTreatments.find(
              (t) => t.selectedTimeSlot
                && t.selectedTimeSlot.spaSoftId === treatment.treatmentId
            )

            return {
              ...treatment,
              ...selectedTreatment,
              isActive: true
            }
          })
        }))
      commit('UPDATE_RESERVATION_DETAILS_DATA', {
        guestAvailabilities,
        date: state.selectedDate,
        propertyData: state.propertyData,
        hotelCode: state.hotelCode,
        confirmationCode: state.confirmationCode,
      })
    },

    async spaCheckoutAction({ state, commit, dispatch }, checkOutData) {
      commit('UPDATE_SPA_LOADING', true)
      sessionStorageHelper.set(SESSION_STORAGE_KEYS.SPA_PURCHASE_TRACKED, false)
      try {
        const { data, status } = await axios.post(
          getApiUrl(`/api/v1/spasoft/${state.hotelCode}/checkout`),
          checkOutData
        )
        if (data.timetableOption && status === 200) {
          commit('UPDATE_SELECTED_ITINERARY', data.timetableOption)
          commit('UPDATE_CONFIRMATION_CODE', data.referenceNumber)
          dispatch('generateReservationDetailsData')
        }
        return data
      } catch (error) {
        commit('UPDATE_SPA_LOADING', false)
        await dispatch('handleApiError', { error, actionName: 'spaCheckoutAction' })
        return error
      } finally {
        commit('UPDATE_SPA_LOADING', false)
      }
    },

    async cancelTreatments({ state, dispatch, commit }, {
      bookingIds = [], cancelAll = false, hotelCode = null
    }) {
      commit('UPDATE_SPA_LOADING', true)
      commit('UPDATE_CANCELLATION_ERROR', false)
      commit('UPDATE_CANCELLATION_SUCCESSFUL', [])
      const bookingIdsToCancel = cancelAll ? state.reservationDetailsData.guestAvailabilities.flatMap((guest) => guest.itinerary.map((treatment) => treatment.bookingId)) : bookingIds
      const results = {
        success: [],
        failed: []
      }
      const hotelCodeToUse = hotelCode || state.reservationDetailsData.hotelCode
      try {
        // Process each booking ID individually
        const cancelPromises = bookingIdsToCancel.map(async (bookingId) => {
          try {
            const { data, status } = await axios.delete(
              getApiUrl(`/api/v1/spasoft/${hotelCodeToUse}/delete`),
              {
                data: {
                  bookingId
                }
              }
            )
            if (status === 200) {
              results.success.push(bookingId)
              return { success: true, bookingId, data }
            }
            results.failed.push(bookingId)
            return { success: false, bookingId, data }
          } catch (error) {
            await dispatch('handleApiError', { error, actionName: `cancelTreatment for bookingId: ${bookingId}` })
            results.failed.push(bookingId)
            return { success: false, bookingId, error }
          }
        })
        // Wait for all cancellation requests to complete
        await Promise.all(cancelPromises)

        if (results.success.length > 0) {
          const cancellationMessages = await Promise.all(results.success.map(async (bookingId) => ({
            treatmentName: await dispatch('getTreatmentName', bookingId)
          })))
          dispatch('showCancellationSuccessMessage', { cancellationMessages })

          // If cancelAll is true, handle differently based on success/error ratio
          if (cancelAll) {
            // Check if all cancellations were successful
            const allCancellationsSuccessful = bookingIdsToCancel.length === results.success.length

            if (allCancellationsSuccessful) {
              // When all cancellations are successful, just disable all treatments but keep them in list
              const updatedGuestAvailabilities = state.reservationDetailsData.guestAvailabilities.map((guestItem) => ({
                ...guestItem,
                itinerary: guestItem.itinerary.map((treatment) => ({
                  ...treatment,
                  isActive: false
                }))
              }))

              commit('UPDATE_RESERVATION_DETAILS_DATA', {
                ...state.reservationDetailsData,
                guestAvailabilities: updatedGuestAvailabilities
              })
            } else {
              // If there are some errors, only remove the successfully cancelled treatments
              const updatedGuestAvailabilities = state.reservationDetailsData.guestAvailabilities.map((guestItem) => ({
                ...guestItem,
                itinerary: guestItem.itinerary.filter(
                  (treatment) => !results.success.includes(treatment.bookingId)
                )
              }))

              // Remove any guests who no longer have treatments
              const finalGuestAvailabilities = updatedGuestAvailabilities.filter(
                (guestItem) => guestItem.itinerary.length > 0
              )

              commit('UPDATE_RESERVATION_DETAILS_DATA', {
                ...state.reservationDetailsData,
                guestAvailabilities: finalGuestAvailabilities
              })
            }
          } else {
            // Handle individual cancellations
            results.success.forEach((bookingId) => {
              // Get the guest index and treatment for this booking ID
              let guestIndex = -1
              let treatment = null

              // Find which guest has this treatment
              state.reservationDetailsData?.guestAvailabilities?.forEach((guest, index) => {
                const foundTreatment = guest.itinerary.find((t) => t.bookingId === bookingId && t.isActive)
                if (foundTreatment) {
                  guestIndex = index
                  treatment = foundTreatment
                }
              })

              if (guestIndex === -1 || !treatment) return

              const guestData = state.reservationDetailsData?.guestAvailabilities?.[guestIndex]
              const totalGuests = state.reservationDetailsData?.guestAvailabilities?.length
              const activeTreatments = guestData?.itinerary?.filter((t) => t.isActive)
              const isLastTreatmentForGuest = activeTreatments.length === 1 && activeTreatments[0].bookingId === bookingId

              if (totalGuests > 1 && isLastTreatmentForGuest) {
                // Scenario 1: Multiple guests and this is the last treatment for this guest
                // Remove the entire guest
                commit('UPDATE_RESERVATION_DETAILS_DATA', {
                  ...state.reservationDetailsData,
                  guestAvailabilities: state.reservationDetailsData?.guestAvailabilities?.filter((_, idx) => idx !== guestIndex)
                })
              } else if (!isLastTreatmentForGuest) {
                // Scenario 2: Guest has multiple treatments (single or multiple guests)
                // Remove this treatment from the list
                commit('UPDATE_RESERVATION_DETAILS_DATA', {
                  ...state.reservationDetailsData,
                  guestAvailabilities: state.reservationDetailsData?.guestAvailabilities?.map((guestItem, idx) => {
                    if (idx === guestIndex) {
                      return {
                        ...guestItem,
                        itinerary: guestItem?.itinerary?.filter((t) => t.bookingId !== bookingId)
                      }
                    }
                    return guestItem
                  })
                })
              } else {
                // Scenario 3: Single guest with single treatment
                // Set treatment to inactive but keep it in the list
                dispatch('disableTreatmentFromReservationDetailsData', { bookingId })
              }
            })
          }
        }
        if (results.failed.length > 0) {
          commit('UPDATE_CANCELLATION_ERROR', true)
        }
        return results
      } catch (error) {
        await dispatch('handleApiError', { error, actionName: 'cancelTreatment' })
        commit('UPDATE_CANCELLATION_ERROR', true)
        return { success: [], failed: bookingIds, error }
      } finally {
        commit('UPDATE_SPA_LOADING', false)
        window.scrollTo(0, 0)
      }
    },
    getTreatmentName({ state }, bookingId) {
      return state.reservationDetailsData?.guestAvailabilities?.flatMap((guest) => guest.itinerary).find((treatment) => treatment.bookingId === bookingId)?.sitecoreTreatmentDetails?.title || ''
    },
    showCancellationSuccessMessage({ commit }, { cancellationMessages }) {
      commit('UPDATE_CANCELLATION_SUCCESSFUL', cancellationMessages)
      setTimeout(() => {
        commit('UPDATE_CANCELLATION_SUCCESSFUL', [])
      }, 5000)
    },
    disableTreatmentFromReservationDetailsData({ state, commit }, { bookingId }) {
      commit('UPDATE_RESERVATION_DETAILS_DATA', {
        ...state.reservationDetailsData,
        guestAvailabilities: state.reservationDetailsData.guestAvailabilities.map((guest) => ({ ...guest, itinerary: guest.itinerary.map((treatment) => (treatment.bookingId === bookingId ? { ...treatment, isActive: false } : treatment)) }))
      })
    },
    removeTreatmentFromGuest({ state, commit }, { guestIndex, treatmentId }) {
      commit('UPDATE_GUEST_SELECTED_TREATMENTS', {
        guest: guestIndex,
        selectedTreatments: state.guestsSelectedData[guestIndex].selectedTreatments.map((treatment) => (treatment.uuid === treatmentId ? { ...treatment, isActive: false } : treatment))
      })
    },
    removeTreatmentFromItinerary({ state, commit }, { guestIndex, bookingId }) {
      commit('UPDATE_SELECTED_ITINERARY', {
        ...state.selectedItinerary,
        guestAvailabilities: state.selectedItinerary.guestAvailabilities.map((guestAvailability, index) => (index === guestIndex ? { ...guestAvailability, itinerary: guestAvailability.itinerary.filter((treatment) => treatment.bookingId !== bookingId) } : guestAvailability))
      })
    },
    updateSelectedItineraryTrackingData({ commit }, trackingData) {
      commit('UPDATE_SELECTED_ITINERARY_TRACKING_DATA', trackingData)
    },
    async cancelAvailableItineraries({ state, commit, dispatch }) {
      try {
        if (state.availableItinerary.length === 0) {
          return
        }
        const availableItinerariesToCancel = state.availableItinerary.flatMap((availability) => availability.guestAvailabilities.flatMap((guest) => guest.itinerary.map((treatment) => treatment.bookingId))).flat()
        await dispatch('cancelTreatments', { bookingIds: availableItinerariesToCancel, hotelCode: state.hotelCode })
      } catch (error) {
        await dispatch('handleApiError', { error, actionName: 'cancelAvailableItineraries' })
      } finally {
        commit('UPDATE_ITINERARY_AVAILABILITY', [])
      }
    },

    trackSpaViewCart({ state, getters, rootGetters }) {
      const mapTreatmentToProduct = (treatment, guestNumber) => ({
        guestNumber: Number(guestNumber) + 1,
        productType: 'Treatment',
        treatmentName: treatment.sitecoreTreatmentDetails.title,
        category: treatment.treatmentCategory?.title,
        duration: treatment.selectedTimeSlot?.duration,
        price: treatment.selectedTimeSlot?.apiTreatmentDetails?.price?.totalCalculated,
        preferredTherapistGender: treatment.selectedTimeSlot?.preferredGender
      })

      const productsList = Object.entries(state.guestsSelectedData)
        .flatMap(([guestNumber, guestData]) => (guestData.selectedTreatments || [])
          .map((treatment) => mapTreatmentToProduct(treatment, guestNumber)))

      this._vm.$track(getters.getBookingTrackingSpaSoft({
        event: 'spaViewCart',
        hotelCode: state.hotelCode,
        stepNumber: 1.3,
        cart: {
          numberOfGuests: state.numberOfGuests,
          date: state.selectedDate,
          preferredStartTime: state.preferredStartTime,
          cartTotalPrice: getters.getTotalCost({
            isReservation: false, formatted: false, noSymbol: true, withIsoCode: false
          }),
          currency: rootGetters['currency/orSelected'](state.propertyData?.currency?.isoCode) || null,
          products: productsList
        }
      }))
    },
  }
}
